import type { SyncStatus } from '@repwise/sync';

/**
 * Data-safety nudges (US-18, J11). Pure, so the thresholds are tested rather than eyeballed.
 * - Pending for more than a day: the changes exist only on this phone.
 * - No backup for 14+ days: last sync (signed in) or last exported file (no account on this build).
 */
const DAY = 86_400_000;
export const PENDING_NUDGE_MS = DAY;
export const BACKUP_WARN_MS = 14 * DAY;
export const LAST_EXPORT_KEY = 'lastExportAt';

const days = (ms: number) => Math.max(1, Math.floor(ms / DAY));

export type Nudge = { kind: 'pending' | 'backup' | 'attention' | 'retry'; text: string; to: string };

/** "in under a minute" · "in 4 min" · "in 1 h 5 min" (ADR-0022 countdown). */
export function retryIn(nextAt: number, now: number): string {
  const s = Math.max(0, Math.round((nextAt - now) / 1000));
  if (s < 60) return 'in under a minute';
  const m = Math.ceil(s / 60);
  return m < 60 ? `in ${m} min` : `in ${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
}

export function dataNudge(o: { status: SyncStatus | null; now: number; syncConfigured: boolean; lastExportAt?: number; hasWorkouts: boolean; firstUseAt?: number }): Nudge | null {
  const s = o.status;
  if (s && s.deadLetters > 0) return { kind: 'attention', text: `${s.deadLetters} change${s.deadLetters === 1 ? '' : 's'} couldn't sync. Save a copy so nothing is lost.`, to: '/settings/unsynced' };
  // No connection (the basement): nothing is "paused"; it syncs as soon as the phone is back online.
  if (s && s.pending > 0 && s.deviceOffline && !(s.oldestPendingAt !== undefined && o.now - s.oldestPendingAt > PENDING_NUDGE_MS)) {
    return { kind: 'pending', text: `${s.pending} change${s.pending === 1 ? '' : 's'} waiting to sync. They'll sync when you're back online.`, to: '/settings' };
  }
  // ADR-0022: a failed sync is waiting to retry. Say when, and offer Sync now (the banner adds the button).
  if (s && s.pending > 0 && s.retry && s.retry.nextAt > o.now) {
    const what = `${s.pending} change${s.pending === 1 ? '' : 's'} waiting to sync`;
    return { kind: 'retry', text: s.retry.mode === 'open' ? `${what}. Sync paused, retrying ${retryIn(s.retry.nextAt, o.now)}.` : `${what}. Retrying ${retryIn(s.retry.nextAt, o.now)}.`, to: '/settings' };
  }
  if (s && s.pending > 0 && s.oldestPendingAt !== undefined && o.now - s.oldestPendingAt > PENDING_NUDGE_MS) {
    const d = days(o.now - s.oldestPendingAt);
    return { kind: 'pending', text: `${s.pending} change${s.pending === 1 ? '' : 's'} waiting to sync (${d} day${d === 1 ? '' : 's'}). They're only on this phone until you connect.`, to: '/settings' };
  }
  if (!o.hasWorkouts) return null;
  // Signed in but the sync status hasn't loaded yet: unknown isn't "never backed up" (found 14 Oct 2026: a flash of
  // "not backed up for 62 days" each time the Train tab mounted, because it fell back to the first-use date)
  if (o.syncConfigured && !s) return null;
  const last = o.syncConfigured ? s?.lastSyncAt : o.lastExportAt;
  const since = last ?? o.firstUseAt;
  if (since !== undefined && o.now - since > BACKUP_WARN_MS) {
    const d = days(o.now - since);
    return o.syncConfigured
      ? { kind: 'backup', text: `Your workouts haven't been backed up for ${d} days. Connect so Repwise can sync.`, to: '/settings' }
      : { kind: 'backup', text: `No backup for ${d} days. Save a backup file to keep your history safe.`, to: '/settings' };
  }
  return null;
}
